import { create } from 'zustand';
import type {
  AddBlockContext,
  AiSequenceSummary,
  BlockCategory,
  BlockProperties,
  BlockType,
  BottomSheetType,
  CanvasState,
  ChatMessage,
  ConditionMode,
  ConditionPickerContext,
  PendingInsertPreview,
  SequenceNode,
  SidebarType,
  SplitConflictContext,
  ViewMode,
  WorkflowSuggestionContext,
} from './sequenceBuilderTypes';
export type {

  AddBlockContext,
  AiSequenceSummary,
  BlockCategory,
  BlockProperties,
  BlockType,
  BottomSheetType,
  CanvasState,
  ChatMessage,
  ConditionMode,
  ConditionPickerContext,
  PendingInsertPreview,
  SequenceNode,
  SidebarType,
  SplitConflictContext,
  ViewMode,
  WorkflowSuggestionContext,
} from './sequenceBuilderTypes';

const createId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `recall-seq-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export function cloneTree(node: SequenceNode | null): SequenceNode | null {
  if (!node) return null;
  const clone: SequenceNode = {
    ...node,
    properties: { ...node.properties },
    next: cloneTree(node.next),
  };
  if (node.branches) {
    clone.branches = {
      yes: cloneTree(node.branches.yes),
      no: cloneTree(node.branches.no),
    };
  }
  if (node.triggerBranches) {
    clone.triggerBranches = node.triggerBranches.map((branch) => cloneTree(branch)!);
  }
  return clone;
}

export function findNode(root: SequenceNode | null, id: string): SequenceNode | null {
  if (!root) return null;
  if (root.id === id) return root;
  const fromNext = findNode(root.next, id);
  if (fromNext) return fromNext;
  if (root.branches) {
    const fromYes = findNode(root.branches.yes, id);
    if (fromYes) return fromYes;
    const fromNo = findNode(root.branches.no, id);
    if (fromNo) return fromNo;
  }
  if (root.triggerBranches) {
    for (const branch of root.triggerBranches) {
      const found = findNode(branch, id);
      if (found) return found;
    }
  }
  return null;
}

export function getAncestorTypes(
  root: SequenceNode | null,
  targetId: string,
  path: BlockType[] = [],
): BlockType[] | null {
  if (!root) return null;
  if (root.id === targetId) return path;
  const nextPath = [...path, root.type];
  const fromNext = getAncestorTypes(root.next, targetId, nextPath);
  if (fromNext) return fromNext;
  if (root.branches) {
    const fromYes = getAncestorTypes(root.branches.yes, targetId, nextPath);
    if (fromYes) return fromYes;
    const fromNo = getAncestorTypes(root.branches.no, targetId, nextPath);
    if (fromNo) return fromNo;
  }
  if (root.triggerBranches) {
    for (const branch of root.triggerBranches) {
      const found = getAncestorTypes(branch, targetId, nextPath);
      if (found) return found;
    }
  }
  return null;
}

export function getAncestorNodes(
  root: SequenceNode | null,
  targetId: string,
  path: SequenceNode[] = [],
): SequenceNode[] | null {
  if (!root) return null;
  if (root.id === targetId) return path;
  const nextPath = [...path, root];
  const fromNext = getAncestorNodes(root.next, targetId, nextPath);
  if (fromNext) return fromNext;
  if (root.branches) {
    const fromYes = getAncestorNodes(root.branches.yes, targetId, nextPath);
    if (fromYes) return fromYes;
    const fromNo = getAncestorNodes(root.branches.no, targetId, nextPath);
    if (fromNo) return fromNo;
  }
  if (root.triggerBranches) {
    for (const branch of root.triggerBranches) {
      const found = getAncestorNodes(branch, targetId, nextPath);
      if (found) return found;
    }
  }
  return null;
}

function shouldTriggerAddBranch(triggerNode: SequenceNode, newCategory: BlockCategory): boolean {
  if (triggerNode.category !== 'trigger') return false;
  if (newCategory !== 'action') return false;
  if (triggerNode.triggerBranches) return true;
  return triggerNode.next !== null && triggerNode.next.category === 'action';
}

function addTriggerBranch(triggerNode: SequenceNode, newNode: SequenceNode): void {
  if (triggerNode.triggerBranches) {
    triggerNode.triggerBranches = [...triggerNode.triggerBranches, newNode];
  } else {
    const existingChain = triggerNode.next;
    triggerNode.next = null;
    triggerNode.triggerBranches = existingChain ? [existingChain, newNode] : [newNode];
  }
}

export function insertAfter(
  root: SequenceNode | null,
  afterId: string,
  newNode: SequenceNode,
  branch: 'next' | 'yes' | 'no' | `trigger-branch-${number}` = 'next',
): SequenceNode | null {
  const tree = cloneTree(root);
  const target = findNode(tree, afterId);
  if (!target) return tree;

  if (branch === 'next' && shouldTriggerAddBranch(target, newNode.category)) {
    addTriggerBranch(target, newNode);
    return tree;
  }

  if (typeof branch === 'string' && branch.startsWith('trigger-branch-')) {
    const index = Number.parseInt(branch.replace('trigger-branch-', ''), 10);
    if (target.triggerBranches && target.triggerBranches[index] !== undefined) {
      newNode.next = target.triggerBranches[index];
      const updated = [...target.triggerBranches];
      updated[index] = newNode;
      target.triggerBranches = updated;
    }
    return tree;
  }

  if (branch === 'next') {
    newNode.next = target.next;
    target.next = newNode;
  } else if (target.branches && (branch === 'yes' || branch === 'no')) {
    const existing = target.branches[branch];
    newNode.next = existing;
    target.branches[branch] = newNode;
  }

  return tree;
}

export function removeNode(root: SequenceNode | null, removeId: string): SequenceNode | null {
  if (!root) return null;
  if (root.id === removeId) return root.next;

  const tree = cloneTree(root);
  removeInTree(tree, removeId);
  return tree;
}

function removeInTree(node: SequenceNode | null, removeId: string): boolean {
  if (!node) return false;

  if (node.next?.id === removeId) {
    node.next = node.next.next;
    return true;
  }
  if (removeInTree(node.next, removeId)) return true;

  if (node.branches) {
    if (node.branches.yes?.id === removeId) {
      node.branches.yes = node.branches.yes.next;
      return true;
    }
    if (node.branches.no?.id === removeId) {
      node.branches.no = node.branches.no.next;
      return true;
    }
    if (removeInTree(node.branches.yes, removeId)) return true;
    if (removeInTree(node.branches.no, removeId)) return true;
  }

  if (node.triggerBranches) {
    for (let index = 0; index < node.triggerBranches.length; index += 1) {
      const branch = node.triggerBranches[index];
      if (!branch) continue;
      if (branch.id === removeId) {
        const updated = node.triggerBranches.filter((_, branchIndex) => branchIndex !== index);
        if (updated.length === 1) {
          node.next = updated[0] ?? null;
          delete node.triggerBranches;
        } else {
          node.triggerBranches = updated;
        }
        return true;
      }
      if (removeInTree(branch, removeId)) return true;
    }
  }

  return false;
}

export function updateNodeInTree(
  root: SequenceNode | null,
  nodeId: string,
  updates: Partial<Pick<SequenceNode, 'label' | 'properties' | 'type'>>,
): SequenceNode | null {
  const tree = cloneTree(root);
  const target = findNode(tree, nodeId);
  if (!target) return tree;
  if (updates.type !== undefined) target.type = updates.type;
  if (updates.label !== undefined) target.label = updates.label;
  if (updates.properties !== undefined) {
    target.properties = { ...target.properties, ...updates.properties };
  }
  return tree;
}

export function findOwnedCondition(actionNode: SequenceNode): SequenceNode | null {
  const cursor = actionNode.next;
  if (cursor && cursor.category === 'condition') return cursor;
  return null;
}

function severAfterAction(actionNode: SequenceNode): SequenceNode | null {
  const rest = actionNode.next;
  actionNode.next = null;
  return rest;
}

interface RecallSequenceState {
  phase: 'initial' | 'aiChat' | 'canvas';
  aiChatStep: number;
  aiChatAnswers: Record<string, string>;
  aiSequenceSummary: AiSequenceSummary | null;
  canvas: CanvasState;
  rootNode: SequenceNode | null;
  selectedBlockId: string | null;
  highlightedNodeId: string | null;
  activeSidebar: SidebarType;
  addBlockContext: AddBlockContext | null;
  pendingInsertPreview: PendingInsertPreview | null;
  selectedTriggerType: BlockType | null;
  initialChatMessages: ChatMessage[];
  initialChatExpanded: boolean;
  isAiGenerating: boolean;
  editChatMessages: ChatMessage[];
  bottomSheet: BottomSheetType;
  conditionPickerContext: ConditionPickerContext | null;
  workflowSuggestionContext: WorkflowSuggestionContext | null;
  splitConflict: SplitConflictContext | null;
  viewMode: ViewMode;
  easyConditionBranch: 'yes' | 'no';
  isAiBuilding: boolean;
  isDirty: boolean;
  isSaved: boolean;
  lastSavedAt: string | null;
  setViewMode: (mode: ViewMode) => void;
  setEasyConditionBranch: (branch: 'yes' | 'no') => void;
  setPhase: (phase: 'initial' | 'aiChat' | 'canvas') => void;
  setCanvas: (canvas: Partial<CanvasState>) => void;
  selectBlock: (id: string | null) => void;
  setHighlightedNode: (id: string | null) => void;
  getNode: (id: string) => SequenceNode | null;
  getAncestorTypes: (id: string) => BlockType[];
  getAncestorNodes: (id: string) => SequenceNode[];
  addNode: (
    afterId: string,
    node: Omit<SequenceNode, 'id' | 'next'>,
    branch?: 'next' | 'yes' | 'no' | `trigger-branch-${number}`,
  ) => string;
  updateNode: (id: string, updates: Partial<Pick<SequenceNode, 'label' | 'properties' | 'type'>>) => void;
  deleteNode: (id: string) => void;
  addAndCondition: (nodeId: string, type: BlockType, label: string) => void;
  removeAndCondition: (nodeId: string, type: BlockType) => void;
  setRootNode: (node: SequenceNode | null) => void;
  toggleActionCondition: (actionNodeId: string, conditionType: BlockType, conditionLabel: string) => void;
  resolveSplitConflict: (resolution: 'yes' | 'no' | 'cancel') => void;
  removeActionCondition: (actionNodeId: string) => void;
  setConditionMode: (conditionNodeId: string, mode: ConditionMode, selectedPath?: 'yes' | 'no') => void;
  getOwnedCondition: (actionNodeId: string) => SequenceNode | null;
  hasSubtreeAfterAction: (actionNodeId: string) => boolean;
  openSidebar: (type: SidebarType) => void;
  closeSidebar: () => void;
  setAddBlockContext: (ctx: AddBlockContext | null) => void;
  setPendingInsertPreview: (preview: PendingInsertPreview | null) => void;
  setSelectedTrigger: (type: BlockType | null) => void;
  addInitialChatMessage: (msg: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  setInitialChatExpanded: (expanded: boolean) => void;
  setIsAiGenerating: (generating: boolean) => void;
  clearInitialChat: () => void;
  addEditChatMessage: (msg: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  clearEditChat: () => void;
  openBottomSheet: (type: BottomSheetType, ctx?: ConditionPickerContext | WorkflowSuggestionContext) => void;
  closeBottomSheet: () => void;
  markSaved: () => void;
  markDirty: () => void;
  serializeSequence: () => string;
  loadFromJson: (json: string) => void;
  setIsAiBuilding: (building: boolean) => void;
  initSequenceFromAi: (root: SequenceNode) => void;
  resetAll: () => void;
}

export const RECALL_SEQUENCE_BACKUP_KEY = 'elva_recall_sequence_backup';
export const SEQUENCE_BACKUP_KEY = RECALL_SEQUENCE_BACKUP_KEY;

const defaultCanvas: CanvasState = {
  zoom: 1,
  panX: 0,
  panY: 0,
  isLocked: true,
  autoZoom: false,
};

const buildDefaultConditionProperties = (): BlockProperties => ({
  conditionMode: 'branching',
  timerEnabled: true,
  timerMode: 'wait',
  delayAmount: 1,
  delayUnit: 'days',
});

export const useRecallSequenceStore = create<RecallSequenceState>((set, get) => ({
  phase: 'initial',
  viewMode: 'easy',
  aiChatStep: 0,
  aiChatAnswers: {},
  aiSequenceSummary: null,
  canvas: defaultCanvas,
  rootNode: null,
  selectedBlockId: null,
  highlightedNodeId: null,
  activeSidebar: 'none',
  addBlockContext: null,
  pendingInsertPreview: null,
  selectedTriggerType: null,
  initialChatMessages: [],
  initialChatExpanded: false,
  isAiGenerating: false,
  editChatMessages: [],
  bottomSheet: null,
  conditionPickerContext: null,
  workflowSuggestionContext: null,
  splitConflict: null,
  isAiBuilding: false,
  easyConditionBranch: 'yes',
  isDirty: false,
  isSaved: false,
  lastSavedAt: null,

  setIsAiBuilding: (building) => set({ isAiBuilding: building }),
  setViewMode: (mode) => set({ viewMode: mode }),
  setEasyConditionBranch: (branch) => set({ easyConditionBranch: branch }),
  setPhase: (phase) => set({ phase }),
  setCanvas: (updates) => set((state) => ({ canvas: { ...state.canvas, ...updates } })),
  selectBlock: (id) => set({ selectedBlockId: id }),
  setHighlightedNode: (id) => set({ highlightedNodeId: id }),

  getNode: (id) => findNode(get().rootNode, id),
  getAncestorTypes: (id) => getAncestorTypes(get().rootNode, id) ?? [],
  getAncestorNodes: (id) => getAncestorNodes(get().rootNode, id) ?? [],

  addNode: (afterId, nodeData, branch = 'next') => {
    const id = createId();
    const isCondition = nodeData.category === 'condition';
    const newNode: SequenceNode = {
      ...nodeData,
      id,
      next: null,
      ...(isCondition ? { branches: { yes: null, no: null } } : {}),
      ...(isCondition
        ? {
            properties: {
              ...buildDefaultConditionProperties(),
              ...nodeData.properties,
            },
          }
        : {}),
    };
    set((state) => ({
      rootNode: insertAfter(state.rootNode, afterId, newNode, branch),
      isDirty: true,
    }));
    return id;
  },

  updateNode: (id, updates) =>
    set((state) => ({
      rootNode: updateNodeInTree(state.rootNode, id, updates),
      isDirty: true,
    })),

  deleteNode: (id) =>
    set((state) => ({
      rootNode: removeNode(state.rootNode, id),
      selectedBlockId: state.selectedBlockId === id ? null : state.selectedBlockId,
      isDirty: true,
    })),

  addAndCondition: (nodeId, type, label) =>
    set((state) => ({
      rootNode: updateNodeInTree(state.rootNode, nodeId, {
        properties: {
          ...findNode(state.rootNode, nodeId)?.properties,
          andConditions: [
            ...(findNode(state.rootNode, nodeId)?.properties.andConditions ?? []),
            { type, label },
          ],
        },
      }),
      isDirty: true,
    })),

  removeAndCondition: (nodeId, type) =>
    set((state) => ({
      rootNode: updateNodeInTree(state.rootNode, nodeId, {
        properties: {
          ...findNode(state.rootNode, nodeId)?.properties,
          andConditions: (findNode(state.rootNode, nodeId)?.properties.andConditions ?? []).filter(
            (condition) => condition.type !== type,
          ),
        },
      }),
      isDirty: true,
    })),

  setRootNode: (node) => set({ rootNode: node, isDirty: true }),

  getOwnedCondition: (actionNodeId) => {
    const node = findNode(get().rootNode, actionNodeId);
    if (!node || node.category !== 'action') return null;
    return findOwnedCondition(node);
  },

  hasSubtreeAfterAction: (actionNodeId) => {
    const node = findNode(get().rootNode, actionNodeId);
    if (!node || node.category !== 'action') return false;
    return !!node.next;
  },

  toggleActionCondition: (actionNodeId, conditionType, conditionLabel) => {
    const root = get().rootNode;
    const actionNode = findNode(root, actionNodeId);
    if (!actionNode || actionNode.category !== 'action') return;

    if (findOwnedCondition(actionNode)) return;

    if (actionNode.next) {
      set({ splitConflict: { actionNodeId, conditionType, conditionLabel } });
      return;
    }

    const tree = cloneTree(root);
    const target = findNode(tree, actionNodeId);
    if (!target) return;

    target.next = {
      id: createId(),
      type: conditionType,
      category: 'condition',
      label: conditionLabel,
      properties: buildDefaultConditionProperties(),
      next: null,
      branches: { yes: null, no: null },
    };

    set({ rootNode: tree, isDirty: true });
  },

  resolveSplitConflict: (resolution) => {
    const conflict = get().splitConflict;
    if (!conflict) return;
    if (resolution === 'cancel') {
      set({ splitConflict: null });
      return;
    }

    const tree = cloneTree(get().rootNode);
    const target = findNode(tree, conflict.actionNodeId);
    if (!target) {
      set({ splitConflict: null });
      return;
    }

    const rest = severAfterAction(target);
    target.next = {
      id: createId(),
      type: conflict.conditionType,
      category: 'condition',
      label: conflict.conditionLabel,
      properties: buildDefaultConditionProperties(),
      next: null,
      branches: {
        yes: resolution === 'yes' ? rest : null,
        no: resolution === 'no' ? rest : null,
      },
    };

    set({ rootNode: tree, splitConflict: null, isDirty: true });
  },

  removeActionCondition: (actionNodeId) => {
    const tree = cloneTree(get().rootNode);
    const target = findNode(tree, actionNodeId);
    if (!target || target.category !== 'action') return;
    const condition = findOwnedCondition(target);
    if (!condition) return;
    target.next = condition.next;
    set({ rootNode: tree, isDirty: true });
  },

  setConditionMode: (conditionNodeId, mode, selectedPath) => {
    const tree = cloneTree(get().rootNode);
    const target = findNode(tree, conditionNodeId);
    if (!target || target.category !== 'condition') return;

    target.properties = {
      ...target.properties,
      conditionMode: mode,
      ...(mode === 'check' && selectedPath ? { checkSelectedPath: selectedPath } : {}),
    };

    if (mode === 'check' && !target.properties.checkSelectedPath) {
      target.properties.checkSelectedPath = 'yes';
    }

    set({ rootNode: tree, isDirty: true });
  },

  openSidebar: (type) => set({ activeSidebar: type }),
  closeSidebar: () => set({ activeSidebar: 'none', pendingInsertPreview: null }),
  setAddBlockContext: (ctx) => set({ addBlockContext: ctx }),
  setPendingInsertPreview: (preview) => set({ pendingInsertPreview: preview }),
  setSelectedTrigger: (type) => set({ selectedTriggerType: type }),

  addInitialChatMessage: (msg) =>
    set((state) => ({
      initialChatMessages: [...state.initialChatMessages, { ...msg, id: createId(), timestamp: Date.now() }],
    })),
  setInitialChatExpanded: (expanded) => set({ initialChatExpanded: expanded }),
  setIsAiGenerating: (generating) => set({ isAiGenerating: generating }),
  clearInitialChat: () => set({ initialChatMessages: [], initialChatExpanded: false }),

  addEditChatMessage: (msg) =>
    set((state) => ({
      editChatMessages: [...state.editChatMessages, { ...msg, id: createId(), timestamp: Date.now() }],
    })),

  clearEditChat: () => set({ editChatMessages: [] }),

  openBottomSheet: (type, ctx) => {
    if (type === 'condition-picker') {
      set({ bottomSheet: type, conditionPickerContext: ctx as ConditionPickerContext });
      return;
    }
    if (type === 'workflow-suggestion') {
      set({ bottomSheet: type, workflowSuggestionContext: ctx as WorkflowSuggestionContext });
      return;
    }
    if (type === 'unsaved-alert' || type === 'backup-restore') {
      set({ bottomSheet: type });
      return;
    }
    set({ bottomSheet: null, conditionPickerContext: null, workflowSuggestionContext: null });
  },

  closeBottomSheet: () => set({ bottomSheet: null, conditionPickerContext: null, workflowSuggestionContext: null }),

  markSaved: () => {
    try {
      localStorage.removeItem(RECALL_SEQUENCE_BACKUP_KEY);
    } catch {
      // Ignore storage errors.
    }
    set({ isDirty: false, isSaved: true, lastSavedAt: new Date().toISOString() });
  },

  markDirty: () => set({ isDirty: true }),

  serializeSequence: () =>
    JSON.stringify({
      rootNode: get().rootNode,
      phase: get().phase,
      canvas: get().canvas,
      savedAt: new Date().toISOString(),
    }),

  loadFromJson: (json) => {
    try {
      const data = JSON.parse(json) as {
        rootNode?: SequenceNode | null;
        phase?: 'initial' | 'aiChat' | 'canvas';
        canvas?: CanvasState;
      };
      set({
        rootNode: data.rootNode ?? null,
        phase: data.phase ?? 'canvas',
        canvas: data.canvas ?? defaultCanvas,
        isDirty: true,
        isSaved: false,
      });
    } catch {
      // Ignore malformed JSON.
    }
  },

  initSequenceFromAi: (root) =>
    set({
      rootNode: root,
      phase: 'canvas',
      viewMode: 'easy',
      isAiBuilding: false,
      canvas: { ...defaultCanvas, isLocked: false },
      initialChatMessages: [],
      initialChatExpanded: false,
      selectedTriggerType: null,
      aiChatStep: 0,
      aiChatAnswers: {},
      aiSequenceSummary: null,
      isDirty: true,
      isSaved: false,
    }),

  resetAll: () =>
    set({
      phase: 'initial',
      viewMode: 'easy',
      aiChatStep: 0,
      aiChatAnswers: {},
      aiSequenceSummary: null,
      canvas: defaultCanvas,
      rootNode: null,
      selectedBlockId: null,
      highlightedNodeId: null,
      activeSidebar: 'none',
      addBlockContext: null,
      pendingInsertPreview: null,
      selectedTriggerType: null,
      initialChatMessages: [],
      initialChatExpanded: false,
      isAiGenerating: false,
      editChatMessages: [],
      bottomSheet: null,
      conditionPickerContext: null,
      workflowSuggestionContext: null,
      splitConflict: null,
      isAiBuilding: false,
      easyConditionBranch: 'yes',
      isDirty: false,
      isSaved: false,
      lastSavedAt: null,
    }),
}));

